import { Box, Button, Card, CardContent, FormControl, FormHelperText, Grid, InputLabel, Link, MenuItem, Select, SelectChangeEvent, Stack, Typography, RadioGroup, FormControlLabel, Radio, Alert, Checkbox } from '@mui/material'
import { Download, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { useContext, useState, useEffect, FunctionComponent } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { IconPicker } from '../../../../../presentation-components/display-elements-components/icon-picker'
import { SelectableCard } from '../../../../../presentation-components/settings-components/data-import/selectable-card'
import { IconName } from '../../../../../../shared/interfaces/icon-picker.model'
import FileUpload from '../../../../../presentation-components/miscellaneous-components/file-upload'
import { ReportPeriodContext } from '../../../../../../context/report-periods/report-periods'
import { downloadExcel, getImportFileType, getTemplateFileName } from '../../../../../../shared/utils/import-utils'
import ConversionSourceService from '../../../../../../shared/services/conversion-source/conversion-source.service'
import FileUploadService from '../../../../../../shared/services/file-upload/file-upload.service'
import { IConversionSource, IConversionYear } from '../../../../../../shared/services/conversion-source/conversion-source.model'
import { ScreenLabelsContext } from '../../../../../../context/screen-labels/screen-labels'
import { UserNotificationsContext } from '../../../../../../context/user-notifications/user-notifications'
import { UserDetailsContext } from '../../../../../../context/user-details/user-details'
import SubsidiaryService from '../../../../../../shared/services/subsidiary/subsidiary.service'
import { ISubsidiary } from '../../../../../../shared/services/subsidiary/subsidiary.model'
import { TooltipHover } from '../../../../../presentation-components/miscellaneous-components/tooltip-hover'
import { TooltipButton } from '../../../../../presentation-components/miscellaneous-components/tooltip-button'
import { BillType } from '../../../../../../shared/services/file-upload/file-upload.model'

export const UploadMethodStep: FunctionComponent<{
  selectedDataType: string | null
  uploadMode: 'template' | 'custom' | 'bills' | null
  emissionType: string
  selectedReportPeriodId: string
  onUploadModeSelect: (mode: 'template' | 'custom' | 'bills') => void
  onEmissionTypeChange: (event: SelectChangeEvent<string>) => void
  onReportPeriodChange: (event: SelectChangeEvent<string>) => void
  onFileUpload: (file: any) => void
  templateType?: 'rules' | 'manual'
  onTemplateTypeChange?: (type: 'rules' | 'manual') => void
  isCustomAllowed: boolean
  showReportPeriodError?: boolean
  linkToExistingEmission?: boolean
  onLinkToExistingEmissionChange?: (value: boolean) => void
  importMode?: 'create' | 'update'
  importMatchKey?: 'name' | 'contactSupplierReference'
  onImportModeChange?: (mode: 'create' | 'update') => void
  onImportMatchKeyChange?: (key: 'name' | 'contactSupplierReference') => void
  isBillAllowed?: boolean
  billType?: BillType
  onBillTypeChange?: (type: BillType) => void
  stagedFiles?: any[]
  onRemoveFile?: (index: number) => void
  selectedConversionSourceId?: string
  selectedConversionYear?: string
  onConversionSourceChange?: (id: string) => void
  onConversionYearChange?: (year: string) => void
  pinFactorYear?: boolean
  onPinFactorYearChange?: (value: boolean) => void
}> = ({
  selectedDataType,
  uploadMode,
  emissionType,
  selectedReportPeriodId,
  onUploadModeSelect,
  onEmissionTypeChange,
  onReportPeriodChange,
  onFileUpload,
  templateType = 'rules',
  onTemplateTypeChange,
  isCustomAllowed,
  showReportPeriodError = false,
  linkToExistingEmission = false,
  onLinkToExistingEmissionChange,
  importMode = 'create',
  importMatchKey = 'name',
  isBillAllowed = false,
  billType = 'energy',
  onBillTypeChange,
  stagedFiles = [],
  onRemoveFile,
  onImportModeChange,
  onImportMatchKeyChange,
  selectedConversionSourceId = '',
  selectedConversionYear = '',
  onConversionSourceChange,
  onConversionYearChange,
  pinFactorYear = false,
  onPinFactorYearChange,
}) => {
    //Service
    const conversionSourceService = new ConversionSourceService()
    const fileUploadService = new FileUploadService()
    const subsidiaryService = SubsidiaryService.getInstance()
    //Context
    const reportPeriodContext = useContext(ReportPeriodContext)
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const userNotificationsContext = useContext(UserNotificationsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    //State
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>([])
    const [availableYears, setAvailableYears] = useState<IConversionYear[]>([])
    const [uploadError, setUploadError] = useState<string | null>(null)
    const [subsidiary, setSubsidiary] = useState<ISubsidiary | null>(null)
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false)
    const emissionTypes = [
      { value: 'activity', label: 'Activity Based', icon: IconName.AssignmentOutlined },
      { value: 'direct', label: 'Pre-Calculated', icon: IconName.CalculateOutlined },
      { value: 'spend', label: 'Spend Based', icon: IconName.CurrencyPound },
      { value: 'products', label: 'Product Based', icon: IconName.Inventory2Outlined }
    ]

    // The factor source and year are needed to build a manual-activity or spend template, so if
    // they sit unset behind a collapsed Advanced options disclosure the download button would be
    // disabled with no visible reason. Open the disclosure whenever the current selection makes
    // them required and one is still missing; the user can still collapse it.
    const factorSelectionRequiredForTemplate = selectedDataType === 'emissions' && uploadMode === 'template' &&
      ((emissionType === 'activity' && templateType === 'manual') || emissionType === 'spend')

    useEffect(() => {
      if (!isCustomAllowed && uploadMode === 'custom') {
        onUploadModeSelect('template')
      }
    }, [isCustomAllowed, uploadMode, onUploadModeSelect])

    useEffect(() => {
      if (factorSelectionRequiredForTemplate && (!selectedConversionSourceId || !selectedConversionYear)) {
        setShowAdvanced(true)
      }
    }, [factorSelectionRequiredForTemplate, selectedConversionSourceId, selectedConversionYear])

    // Default the report period to the one the user last worked in, so the most common case needs
    // no extra click. Only open periods are offered in the select, so fall back to the last
    // selected OPEN period when the overall last-selected one is locked.
    useEffect(() => {
      if (selectedDataType !== 'emissions' || selectedReportPeriodId) {
        return
      }
      const openPeriods = reportPeriodContext.openReportPeriods ?? []
      const preferred = [reportPeriodContext.lastSelectedReportId, reportPeriodContext.lastSelectedOpenReportId]
        .find(id => id && openPeriods.some(period => period.id === id))
      if (preferred) {
        onReportPeriodChange({ target: { value: preferred } } as any)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDataType, selectedReportPeriodId, reportPeriodContext.lastSelectedReportId, reportPeriodContext.lastSelectedOpenReportId, reportPeriodContext.openReportPeriods])

    useEffect(() => {
      if (userDetailsContext.userDetails?.defaultSubsidiaryId) {
        subsidiaryService.getOne(userDetailsContext.userDetails.defaultSubsidiaryId)
          .then(setSubsidiary)
          .catch((err) => {
            console.log(err)
          })
      }
    }, [userDetailsContext.userDetails?.defaultSubsidiaryId])

    useEffect(() => {
      if (selectedDataType === 'emissions' && emissionType === 'activity') {
        conversionSourceService.getAll(true, 'conversion')
          .then(setConversionSources)
          .catch((err) => {
            console.log(err)
          })
      } else if (selectedDataType === 'emissions' && emissionType === 'spend') {
        conversionSourceService.getAll(true, 'spendBasedConversion')
          .then(setConversionSources)
          .catch((err) => {
            console.log(err)
          })
      } else {
        setConversionSources([])
      }
    }, [selectedDataType, emissionType])

    useEffect(() => {
      if (conversionSources.length > 0 && !selectedConversionSourceId && subsidiary) {
        if (selectedDataType === 'emissions' && emissionType === 'activity' && templateType === 'manual') {
          if (subsidiary.defaultConversionSourceId && conversionSources.find(s => s.id === subsidiary.defaultConversionSourceId)) {
            onConversionSourceChange?.(subsidiary.defaultConversionSourceId)
          }
        } else if (selectedDataType === 'emissions' && emissionType === 'spend') {
          if (subsidiary.defaultSpendBasedConversionSourceId && conversionSources.find(s => s.id === subsidiary.defaultSpendBasedConversionSourceId)) {
            onConversionSourceChange?.(subsidiary.defaultSpendBasedConversionSourceId)
          }
        }
      }
    }, [conversionSources, subsidiary, selectedDataType, emissionType, templateType, selectedConversionSourceId])

    useEffect(() => {
      // The wizard owns the selected source/year (they are also sent with the upload when the
      // factor year is pinned); the year list is local and always follows the selected source, so
      // it survives the step remounting with a source already chosen.
      if (!selectedConversionSourceId) {
        setAvailableYears([])
        return
      }
      conversionSourceService.getOne(selectedConversionSourceId, '?includeYears=true&includePendingYears=true')
        .then(src => {
          const yrs = (src?.conversionYears || []).filter((y: IConversionYear) => (y.active ?? (y as any).isActive))
          const sorted = yrs.sort((a, b) => Number(a.year) - Number(b.year))
          setAvailableYears(sorted)
          // Prefill the year the way manual entry does: the reporting period already knows its
          // conversion year, so asking for it again is a question with one sensible answer.
          // The period's year is used when this source publishes it, otherwise the latest
          // active year (the same clamping the factor search applies). Only ever fills an
          // empty selection: a hand-picked year is never overridden.
          if (!selectedConversionYear && sorted.length > 0) {
            const period = (reportPeriodContext.openReportPeriods ?? []).find(p => p.id === selectedReportPeriodId)
            const periodYear = period?.conversionFiguresYear ? String(period.conversionFiguresYear) : undefined
            const match = periodYear && sorted.some(y => String(y.year) === periodYear)
              ? periodYear
              : String(sorted[sorted.length - 1].year)
            onConversionYearChange?.(match)
          }
        })
        .catch((err) => {
          console.log(err)
        })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedConversionSourceId, selectedReportPeriodId])


    const handleDownloadTemplate = async () => {
      if (!selectedDataType) return
      try {
        if (
          selectedDataType === 'emissions' &&
          ((emissionType === 'activity' && templateType === 'manual') || emissionType === 'spend') &&
          (!selectedConversionSourceId || !selectedConversionYear)
        ) {
          return;
        }
        const fileType = getImportFileType(selectedDataType, emissionType, templateType)
        const templateTypeParam = selectedDataType === 'emissions' && emissionType === 'activity' ? templateType : undefined
        const conversionSourceIdParam = (selectedDataType === 'emissions' && emissionType === 'activity' && templateType === 'manual') || (selectedDataType === 'emissions' && emissionType === 'spend') ? selectedConversionSourceId : undefined
        const conversionYearParam = (selectedDataType === 'emissions' && emissionType === 'activity' && templateType === 'manual') || (selectedDataType === 'emissions' && emissionType === 'spend') ? selectedConversionYear : undefined

        let queryParams = ''
        if (templateTypeParam) {
          queryParams += `?templateType=${templateTypeParam}`
        }
        if (conversionSourceIdParam) {
          queryParams += queryParams ? `&conversionSourceId=${conversionSourceIdParam}` : `?conversionSourceId=${conversionSourceIdParam}`
        }
        if (conversionYearParam) {
          queryParams += queryParams ? `&conversionYear=${conversionYearParam}` : `?conversionYear=${conversionYearParam}`
        }

        const response = await fileUploadService.getExcelTemplate(fileType, queryParams)
        const fileName = getTemplateFileName(fileType)
        downloadExcel(response, fileName)
      } catch (error: any) {
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
        console.error('Failed to download template:', error);
      }
    }

    const getDataTypeTitle = () => {
      const titles: { [key: string]: string } = {
        'emissions': 'emissions',
        'locations': screenLabelsContext.getLabel('location', { plural: true }),
        'vehicles': screenLabelsContext.getLabel('vehicle', { plural: true }),
        'products': screenLabelsContext.getLabel('product', { plural: true }),
        'projects': screenLabelsContext.getLabel('project', { plural: true }),
        'suppliers': screenLabelsContext.getLabel('supplier', { plural: true }),
      }
      return titles[selectedDataType || ''] || 'data'
    }

    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'left' }}>
          Upload your {getDataTypeTitle()}
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', textAlign: 'left' }}>
          Choose how you'd like to upload your {getDataTypeTitle()}.
        </Typography>
        {selectedDataType === 'emissions' && (
          <Box sx={{ mb: 4, maxWidth: 880 }}>
            <FormControl fullWidth error={showReportPeriodError && !selectedReportPeriodId}>
              <InputLabel>Report Period</InputLabel>
              <Select
                value={selectedReportPeriodId || ''}
                onChange={onReportPeriodChange}
                label="Report Period"
              >
                {[...(reportPeriodContext.openReportPeriods ?? [])]
                  .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
                  .map((period) => (
                    <MenuItem key={period.id} value={period.id}>
                      {period.friendlyName}
                    </MenuItem>
                  ))}
              </Select>
              {showReportPeriodError && !selectedReportPeriodId && (
                <FormHelperText>
                  Report period is required
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        )}
        {selectedDataType === 'emissions' && (
          <Box sx={{ mb: 4, maxWidth: 880 }}>
            <Stack spacing={3}>
              <Box>
                <Grid container spacing={2}>
                  {emissionTypes.map((type) => (
                    <Grid item xs={3} key={type.value}>
                      <TooltipHover
                        title={type.label}
                        text={type.label === 'Product Based' ? 'Upload emissions linked to purchased products or materials - e.g. kg of concrete, tonnes of steel, or unit-based items with known emission factors.' :
                          type.label === 'Spend Based' ? 'Upload transactions where only cost values are known. We’ll calculate emissions automatically using £-based conversion factors.' :
                            type.label === 'Pre-Calculated' ? 'Use this option when you already have emissions calculated - e.g. kgCO₂e from a travel platform, utility report, or sustainability tool. We’ll import the emissions as-is without further calculation.' :
                              type.label === 'Activity Based' ? 'Upload data that represents real usage - e.g. kWh of electricity, litres of fuel, miles travelled, or physical quantities. This gives more accurate emissions than spend-based data.' :
                                ''}
                        placement='top'
                        backgroundColor='common.white'
                      >
                        <Card
                          onClick={() => onEmissionTypeChange({ target: { value: type.value } } as any)}
                          sx={{
                            cursor: 'pointer',
                            boxShadow: 'none',
                            borderWidth: '2px',
                            borderStyle: 'solid',
                            borderColor: emissionType === type.value ? 'primary.main' : 'lightGrey.main',
                            borderRadius: '10px',
                            backgroundColor: emissionType === type.value ? 'secondary.main' : 'common.white',
                            '&:hover': {
                              borderColor: 'primary.main',
                              backgroundColor: emissionType === type.value ? 'secondary.light' : 'action.hover'
                            }
                          }}>
                          <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 80 }}>
                            <Box sx={{ color: 'text.secondary', mb: 1 }}>
                              <IconPicker icon={type.icon} />
                            </Box>
                            <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary', textAlign: 'center' }}>
                              {type.label}
                            </Typography>
                          </CardContent>
                        </Card>
                      </TooltipHover>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Stack>
          </Box>
        )}
        {selectedDataType === 'suppliers' && (
          <Box sx={{ mb: 4, maxWidth: 880 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Import action
            </Typography>
            <Grid container spacing={2} sx={{ mb: importMode === 'update' ? 3 : 0 }}>
              <Grid item xs={6}>
                <SelectableCard
                  align='center'
                  minHeight={100}
                  title='Create suppliers'
                  description='Add new suppliers to your organisation'
                  selected={importMode === 'create'}
                  onClick={() => onImportModeChange?.('create')}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectableCard
                  align='center'
                  minHeight={100}
                  title='Update existing suppliers'
                  description='Update details of suppliers already in your organisation'
                  selected={importMode === 'update'}
                  onClick={() => onImportModeChange?.('update')}
                />
              </Grid>
            </Grid>
            {importMode === 'update' && (
              <FormControl fullWidth>
                <InputLabel>Match suppliers by</InputLabel>
                <Select
                  label="Match suppliers by"
                  value={importMatchKey}
                  onChange={(e) => onImportMatchKeyChange?.(e.target.value as 'name' | 'contactSupplierReference')}
                >
                  <MenuItem value="name">Supplier Name</MenuItem>
                  <MenuItem value="contactSupplierReference">Supplier Reference</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        )}
        <Box sx={{ mb: 4, maxWidth: 880 }}>
          <Grid container spacing={2}>
            <Grid item xs={isBillAllowed ? 4 : 6}>
              <SelectableCard
                align='center'
                minHeight={120}
                fullHeight
                icon={IconName.DescriptionOutlined}
                title='Use Excel template'
                selected={uploadMode === 'template'}
                onClick={() => onUploadModeSelect('template')}
              />
            </Grid>
            <Grid item xs={isBillAllowed ? 4 : 6}>
              <SelectableCard
                align='center'
                minHeight={120}
                fullHeight
                icon={IconName.UploadOutlined}
                title='Use custom spreadsheet'
                selected={uploadMode === 'custom'}
                disabled={!isCustomAllowed}
                onClick={() => onUploadModeSelect('custom')}
              />
            </Grid>
            {isBillAllowed && (
              <Grid item xs={4}>
                <SelectableCard
                  align='center'
                  minHeight={120}
                  fullHeight
                  icon={IconName.ReceiptLongOutlined}
                  title='Upload bills'
                  selected={uploadMode === 'bills'}
                  onClick={() => onUploadModeSelect('bills')}
                />
              </Grid>
            )}
          </Grid>
        </Box>
        <Box sx={{ maxWidth: 880 }}>
          {(uploadMode === 'template' || uploadMode === 'custom') && selectedDataType === 'emissions' && emissionType === 'activity' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                How should rows be categorised?
              </Typography>
              <FormControl component="fieldset" disabled={uploadMode === 'custom'}>
                <RadioGroup
                  value={uploadMode === 'custom' ? 'rules' : templateType}
                  onChange={(e) => onTemplateTypeChange?.(e.target.value as 'rules' | 'manual')}
                  row
                >
                  <FormControlLabel
                    value="rules"
                    control={<Radio />}
                    label="Use rules (recommended)"
                  />
                  <FormControlLabel
                    value="manual"
                    control={<Radio />}
                    label="Manual category selection"
                  />
                </RadioGroup>
              </FormControl>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Rules match each row to the right emission category automatically. Manage yours in{' '}
                <Link component={RouterLink} to="/settings/data-upload/custom-rules">
                  {'Settings > Data import > Custom Rules'}
                </Link>.
              </Typography>
              {uploadMode === 'custom' && (
                <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                  Custom spreadsheets are always categorised with rules, so this cannot be changed.
                </Typography>
              )}
            </Box>
          )}
          {selectedDataType === 'emissions' && (
            (uploadMode === 'template' && ((emissionType === 'activity' && templateType === 'manual') || emissionType === 'spend')) ||
            (uploadMode === 'custom' && emissionType === 'spend')
          ) && (
            <Box sx={{ mb: 4, mt: emissionType === 'activity' ? 2 : 0 }}>
              {/* Same disclosure pattern as EmissionsInputAdvanced: a clickable row that toggles the
                  section. Factor source, year and the pin option are settings most uploads never
                  touch, so they start collapsed; the effect above opens them when the chosen
                  template cannot be built without them. */}
              <Box
                onClick={() => setShowAdvanced(!showAdvanced)}
                sx={{ display: 'inline-block', cursor: 'pointer', mb: showAdvanced ? 2 : 0 }}
              >
                <Stack direction='row'>
                  {showAdvanced ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  <Typography>Advanced options</Typography>
                </Stack>
              </Box>
              {showAdvanced && <Stack spacing={2}>
                {(uploadMode === 'template' || pinFactorYear) && (
                  <>
                    <FormControl fullWidth>
                      <InputLabel>Emission Factor Source</InputLabel>
                      <Select
                        label="Emission Factor Source"
                        value={selectedConversionSourceId}
                        onChange={(e) => {
                          onConversionSourceChange?.(e.target.value as string)
                          onConversionYearChange?.('')
                        }}
                      >
                        {conversionSources.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.source}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {selectedConversionSourceId && (
                      <FormControl fullWidth>
                        <InputLabel>Conversion Year</InputLabel>
                        <Select
                          label="Conversion Year"
                          value={selectedConversionYear}
                          onChange={(e) => onConversionYearChange?.(e.target.value)}>
                          {availableYears.map((y) => (
                            <MenuItem key={y.year} value={String(y.year)}>{y.year}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
                {emissionType === 'spend' && (
                  <Box>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={pinFactorYear}
                          onChange={(event) => onPinFactorYearChange?.(event.target.checked)}
                        />
                      )}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">
                            Use a single factor year for every row in this upload
                          </Typography>
                          <TooltipButton
                            text="When ticked, every row is calculated with factors from the selected year, whatever its invoice date. Useful when a financial year should be reported against a single factor set."
                            placement='right'
                            noPadding
                          />
                        </Box>
                      )}
                    />
                  </Box>
                )}
              </Stack>}
            </Box>
          )}
          {uploadMode === 'template' && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={handleDownloadTemplate}
                sx={{ minWidth: 200, mb: 2 }}
                disabled={
                  (selectedDataType === 'emissions' && emissionType === 'activity' && templateType === 'manual' && (!selectedConversionSourceId || !selectedConversionYear)) ||
                  (selectedDataType === 'emissions' && emissionType === 'spend' && (!selectedConversionSourceId || !selectedConversionYear))
                }
                startIcon={<Download />}
              >
                <Typography>
                  Download template
                </Typography>
              </Button>
              {factorSelectionRequiredForTemplate && (!selectedConversionSourceId || !selectedConversionYear) && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  Choose an emission factor source and year under Advanced options to build this template.
                </Typography>
              )}
            </Box>
          )}
          {selectedDataType === 'emissions' && emissionType === 'activity' && (
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={linkToExistingEmission}
                    onChange={(event) => onLinkToExistingEmissionChange?.(event.target.checked)}
                  />
                )}
                label={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">
                      Link imported activity rows to existing spend-based emissions
                    </Typography>
                    <TooltipButton
                      text="During import review, we will look for likely existing spend-based emissions to link to using report period, activity period, operational context, and spend-based group."
                      placement='right'
                      noPadding
                    />
                  </Box>
                )}
              />
            </Box>
          )}
          {/* {uploadMode !== 'custom' && (
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', textAlign: 'left' }}>*If you're using an older version of our template, please ensure the column names match the latest version</Typography>
          )} */}
          {uploadMode === 'bills' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                What kind of bills are these?
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                We work out the rest from each bill. You can correct any document on the next screen.
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={billType === 'fuel' ? 'fuel' : 'energy'}
                  onChange={(e) => onBillTypeChange?.(e.target.value as BillType)}
                  row
                >
                  <FormControlLabel value="energy" control={<Radio />} label="Energy bill (electricity, gas, or both)" />
                  <FormControlLabel value="fuel" control={<Radio />} label="Fuel (fuel card or receipt)" />
                </RadioGroup>
              </FormControl>
              <Alert severity="info" sx={{ mt: 1 }}>
                We read the consumption, dates and meter details off each bill, and work out whether
                it's electricity, gas or both: a combined bill gets a record for each fuel. Electricity
                and gas bills can be mixed in one upload. You'll check everything we found before
                anything is imported. Water bills aren't supported yet.
              </Alert>
            </Box>
          )}
          <Box>
            {!!uploadError && (
              <Alert severity="info" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
                {uploadError}
              </Alert>
            )}
            {uploadMode === 'bills' ? (
              <FileUpload
                document={true}
                multiple={true}
                // Exactly the formats Textract accepts.
                allowedFileTypes={['PDF', 'PNG', 'JPG', 'JPEG', 'TIF', 'TIFF']}
                handleFileUpload={(f: any) => { setUploadError(null); onFileUpload(f); }}
                onError={(msg: string) => setUploadError(msg)}
                typeErrorMessage='Unsupported file type. Please upload a PDF or an image of your bill.'
                maxSizeMb={15}
              />
            ) : (
              <FileUpload
                csv={true}
                excel={true}
                allowedFileTypes={['CSV', 'XLSX']}
                handleFileUpload={(f: any) => { setUploadError(null); onFileUpload(f); }}
                onError={(msg: string) => setUploadError(msg)}
                maxSizeMb={50}
              />
            )}
            {uploadMode === 'bills' && stagedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {stagedFiles.length} {stagedFiles.length === 1 ? 'bill' : 'bills'} ready to upload
                </Typography>
                <Stack spacing={0.5}>
                  {stagedFiles.map((file: any, index: number) => (
                    <Box
                      key={`${file.name}-${index}`}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.75
                      }}
                    >
                      <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0 }}>
                        <IconPicker icon={IconName.ReceiptLongOutlined} scale={0.8} />
                        <Typography variant='body2' noWrap title={file.name}>{file.name}</Typography>
                      </Stack>
                      {/* Neutral, not the themed green. The theme defaults every Button to
                          variant='contained' color='primary', so a bare <Button> here is a filled
                          green pill — the treatment reserved for the primary action, which discarding
                          a staged file is the opposite of. Outlined keeps the button affordance while
                          reading as clearly secondary. Colours come from the theme (lightGrey /
                          text.*), and hover darkens rather than lightens. */}
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => onRemoveFile?.(index)}
                        sx={{
                          color: 'text.secondary',
                          borderColor: 'lightGrey.main',
                          '&:hover': { color: 'text.primary', borderColor: 'text.secondary', bgcolor: 'action.hover' },
                        }}
                      >
                        <Typography variant='body2'>Remove</Typography>
                      </Button>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
      </Box >
    )
  }
